<script setup lang="ts">
import { ref, nextTick, onMounted } from "vue";

const triggerEl = ref<HTMLElement | null>(null);
const tooltipEl = ref<HTMLElement | null>(null);

onMounted(async () => {
  await nextTick();
  if (triggerEl.value && tooltipEl.value) {
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
  }
});

function updatePosition() {
  if (!triggerEl.value || !tooltipEl.value) return;

  const trigger = triggerEl.value.getBoundingClientRect();
  const tooltip = tooltipEl.value;

  // Position tooltip relative to trigger
  // Try to position above by default
  let top = trigger.top - tooltip.offsetHeight - 4;
  let left = trigger.left + trigger.width / 2 - tooltip.offsetWidth / 2;

  // If tooltip would go above viewport, position below instead
  if (top < 0) {
    top = trigger.bottom + 4;
  }

  // Keep tooltip within horizontal bounds
  const padding = 8;
  if (left < padding) {
    left = padding;
  } else if (left + tooltip.offsetWidth > window.innerWidth - padding) {
    left = window.innerWidth - tooltip.offsetWidth - padding;
  }

  tooltip.style.position = "fixed";
  tooltip.style.top = `${top}px`;
  tooltip.style.left = `${left}px`;
  tooltip.style.zIndex = "9999";
}
</script>

<template>
  <div ref="triggerEl" class="smart-tooltip-trigger">
    <slot name="trigger" />
  </div>
  <Teleport to="body">
    <div v-if="$slots.content" ref="tooltipEl" class="smart-tooltip-content">
      <slot name="content" />
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.smart-tooltip-trigger {
  display: inline-flex;
}

.smart-tooltip-content {
  position: fixed;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 6px 8px;
  font-size: 0.7rem;
  line-height: 1.3;
  z-index: 9999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 200px;
  word-wrap: break-word;
}
</style>
